import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  Download,
  Eye,
  FileText,
  Pencil,
  Plus,
  Save,
  Search,
  Settings2,
  Trash2,
  Upload,
  Users,
  X
} from "lucide-react";
import { FormEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { Shell } from "../components/Shell";
import { StatCard } from "../components/StatCard";
import type {
  AppUser,
  Assessment,
  CostCenter,
  Dashboard,
  ModuleContent,
  Question,
  QuestionType,
  ResultAttempt,
  SessionUser,
  TrainingModule
} from "../types";
import { readStoredTab } from "../utils/navigation";

type AdminPageProps = {
  user: SessionUser;
  onLogout: () => void;
};

const nav = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard" as const },
  { id: "users", label: "Usuarios", icon: "users" as const },
  { id: "centers", label: "Centros de Custo", icon: "centers" as const },
  { id: "modules", label: "Modulos", icon: "modules" as const },
  { id: "results", label: "Resultados", icon: "results" as const }
];

const ADMIN_ACTIVE_TAB_KEY = "setup_admin_active_tab";
const adminNavIds = nav.map((item) => item.id);

const emptyModuleForm = {
  titulo: "",
  descricao: "",
  ordem: 0,
  status: "draft",
  centros_custo_permitidos: [] as string[],
  visivel_todos: true
};

const emptyContentForm = {
  tipo_conteudo: "text",
  titulo: "",
  texto: "",
  url_arquivo: "",
  ordem: 0,
  obrigatorio: true,
  status: "published",
  centros_custo_permitidos: [] as string[],
  visivel_todos: false
};

const emptyAssessmentForm = {
  titulo: "",
  descricao: "",
  nota_minima: 70,
  status: "published",
  mostrar_resultado: true
};

const emptyQuestionForm = {
  tipo: "multiple_choice",
  enunciado: "",
  alternativas: "",
  resposta_correta: "",
  pontos: 1,
  ordem: 0,
  feedback: "",
  url_imagem: "",
  id_imagem_drive: ""
};

export function AdminPage({ user, onLogout }: AdminPageProps) {
  const [active, setActive] = useState(() => readStoredTab(ADMIN_ACTIVE_TAB_KEY, "dashboard", adminNavIds));
  const [notice, setNotice] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [centers, setCenters] = useState<CostCenter[]>([]);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [results, setResults] = useState<ResultAttempt[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [contents, setContents] = useState<ModuleContent[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [responses, setResponses] = useState<Record<string, unknown>[]>([]);

  const loadAll = useCallback(async () => {
    const [dash, cc, moduleRows, resultRows, userRows] = await Promise.all([
      api.dashboard(),
      api.costCenters(),
      api.modules(),
      api.results(),
      api.users()
    ]);
    setDashboard(dash);
    setCenters(cc);
    setModules(moduleRows);
    setResults(resultRows);
    setUsers(userRows);
    setSelectedModuleId((current) => current || (moduleRows[0]?.id_modulo ?? ""));
    setNotice("");
  }, []);

  const loadModuleDetail = useCallback(async (moduleId: string) => {
    if (!moduleId) return { contentRows: [], assessmentRows: [] };
    const [contentRows, assessmentRows] = await Promise.all([api.contents(moduleId), api.assessments(moduleId)]);
    return { contentRows, assessmentRows };
  }, []);

  const loadQuestions = useCallback(async (assessmentId: string) => {
    if (!assessmentId) return [];
    return api.questions(assessmentId);
  }, []);

  useEffect(() => {
    loadAll().catch((error) => setNotice(error.message));
  }, [loadAll]);

  useEffect(() => {
    window.localStorage.setItem(ADMIN_ACTIVE_TAB_KEY, active);
  }, [active]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(""), 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    let ignore = false;
    setContents([]);
    setAssessments([]);
    setSelectedAssessmentId("");
    setQuestions([]);

    if (!selectedModuleId) return;

    loadModuleDetail(selectedModuleId)
      .then(({ contentRows, assessmentRows }) => {
        if (ignore) return;
        const moduleContents = contentRows.filter((content) => content.id_modulo === selectedModuleId);
        const moduleAssessments = assessmentRows.filter((assessment) => assessment.id_modulo === selectedModuleId);
        setContents(moduleContents);
        setAssessments(moduleAssessments);
        setSelectedAssessmentId(moduleAssessments[0]?.id_avaliacao ?? "");
        setNotice("");
      })
      .catch((error) => {
        if (ignore) return;
        setContents([]);
        setAssessments([]);
        setSelectedAssessmentId("");
        setQuestions([]);
        setNotice(error instanceof Error ? error.message : "Nao foi possivel carregar o modulo.");
      });

    return () => {
      ignore = true;
    };
  }, [selectedModuleId, loadModuleDetail]);

  useEffect(() => {
    let ignore = false;
    setQuestions([]);

    if (!selectedAssessmentId) return;

    loadQuestions(selectedAssessmentId)
      .then((questionRows) => {
        if (ignore) return;
        setQuestions(questionRows.filter((question) => question.id_avaliacao === selectedAssessmentId));
        setNotice("");
      })
      .catch((error) => {
        if (ignore) return;
        setQuestions([]);
        setNotice(error instanceof Error ? error.message : "Nao foi possivel carregar as perguntas.");
      });

    return () => {
      ignore = true;
    };
  }, [selectedAssessmentId, loadQuestions]);

  async function refresh(message = "Atualizado com sucesso.") {
    const moduleId = selectedModuleId;
    const assessmentId = selectedAssessmentId;
    await loadAll();
    if (moduleId) {
      const { contentRows, assessmentRows } = await loadModuleDetail(moduleId);
      const moduleContents = contentRows.filter((content) => content.id_modulo === moduleId);
      const moduleAssessments = assessmentRows.filter((assessment) => assessment.id_modulo === moduleId);
      setContents(moduleContents);
      setAssessments(moduleAssessments);
    }
    if (assessmentId) {
      const questionRows = await loadQuestions(assessmentId);
      setQuestions(questionRows.filter((question) => question.id_avaliacao === assessmentId));
    }
    setNotice(message);
  }

  async function refreshModules(message = "Modulo atualizado.") {
    const moduleRows = await api.modules();
    setModules(moduleRows);

    const nextModuleId = moduleRows.some((module) => module.id_modulo === selectedModuleId)
      ? selectedModuleId
      : moduleRows[0]?.id_modulo ?? "";
    setSelectedModuleId(nextModuleId);

    if (!nextModuleId) {
      setContents([]);
      setAssessments([]);
      setSelectedAssessmentId("");
      setQuestions([]);
      setNotice(message);
      return;
    }

    const { contentRows, assessmentRows } = await loadModuleDetail(nextModuleId);
    const moduleContents = contentRows.filter((content) => content.id_modulo === nextModuleId);
    const moduleAssessments = assessmentRows.filter((assessment) => assessment.id_modulo === nextModuleId);
    setContents(moduleContents);
    setAssessments(moduleAssessments);

    const nextAssessmentId = moduleAssessments.some((assessment) => assessment.id_avaliacao === selectedAssessmentId)
      ? selectedAssessmentId
      : moduleAssessments[0]?.id_avaliacao ?? "";
    setSelectedAssessmentId(nextAssessmentId);
    const questionRows = nextAssessmentId ? await loadQuestions(nextAssessmentId) : [];
    setQuestions(questionRows.filter((question) => question.id_avaliacao === nextAssessmentId));
    setNotice(message);
  }

  async function refreshResults(message = "Correcao salva.") {
    const resultRows = await api.results();
    setResults(resultRows);
    if (selectedAttemptId) setResponses(await api.responses(selectedAttemptId));
    setNotice(message);
  }

  const selectedModule = modules.find((item) => item.id_modulo === selectedModuleId);
  const selectedAssessment = assessments.find((item) => item.id_avaliacao === selectedAssessmentId);
  const activityLabel =
    active === "modules" && selectedModule?.titulo
      ? `Admin: ${selectedModule.titulo}`
      : nav.find((item) => item.id === active)?.label ?? active;

  return (
    <Shell user={user} items={nav} active={active} onNavigate={setActive} onLogout={onLogout} activityLabel={activityLabel}>
      {notice ? <div className="notice">{notice}</div> : null}
      {active === "dashboard" && <DashboardSection dashboard={dashboard} results={results} modules={modules} />}
      {active === "users" && (
        <UsersSection users={users} centers={centers} onChanged={() => refresh("Usuario salvo.")} />
      )}
      {active === "centers" && (
        <CentersSection centers={centers} onChanged={() => refresh("Centro de custo salvo.")} />
      )}
      {active === "modules" && (
        <ModulesSection
          centers={centers}
          modules={modules}
          selectedModule={selectedModule}
          selectedModuleId={selectedModuleId}
          onSelectModule={setSelectedModuleId}
          contents={contents}
          assessments={assessments}
          selectedAssessment={selectedAssessment}
          selectedAssessmentId={selectedAssessmentId}
          onSelectAssessment={setSelectedAssessmentId}
          questions={questions}
          onChanged={() => refreshModules("Modulo atualizado.")}
        />
      )}
      {active === "results" && (
        <ResultsSection
          results={results}
          selectedAttemptId={selectedAttemptId}
          onSelectAttempt={async (id) => {
            setSelectedAttemptId(id);
            setResponses(await api.responses(id));
          }}
          responses={responses}
          onChanged={() => refreshResults("Correcao salva.")}
        />
      )}
    </Shell>
  );
}

function DashboardSection({
  dashboard,
  results,
  modules
}: {
  dashboard: Dashboard | null;
  results: ResultAttempt[];
  modules: TrainingModule[];
}) {
  const [selectedModuleFilter, setSelectedModuleFilter] = useState("");

  if (!dashboard) return <EmptyState title="Carregando" text="Buscando indicadores do painel." />;
  const pendingManual = results.filter((result) => result.status_correcao === "pending_manual").length;
  const moduleOptions = [...modules].sort((a, b) => a.titulo.localeCompare(b.titulo, "pt-BR"));
  const filteredAccesses = selectedModuleFilter
    ? dashboard.ultimos_acessos.filter((access) => matchesModuleAccess(access, selectedModuleFilter, modules))
    : dashboard.ultimos_acessos;

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Visao geral</span>
          <h1>Dashboard administrativo</h1>
        </div>
      </div>
      <div className="stats-grid">
        <StatCard label="Usuarios" value={dashboard.total_usuarios} icon={<Users size={20} />} />
        <StatCard label="Modulos" value={dashboard.total_modulos} icon={<BookOpen size={20} />} />
        <StatCard label="Ativos" value={dashboard.modulos_ativos} icon={<CheckCircle2 size={20} />} />
        <StatCard label="Avaliacoes" value={dashboard.avaliacoes_realizadas} icon={<FileText size={20} />} />
        <StatCard label="Pendentes de correcao" value={pendingManual} icon={<ClipboardCheck size={20} />} />
        <StatCard label="Progresso medio" value={`${dashboard.progresso_medio}%`} icon={<BarChart3 size={20} />} />
      </div>
      <div className="panel dashboard-access-panel">
        <div className="panel-heading">
          <div>
            <span>Tempo de estudo</span>
            <h2>Ultimos acessos</h2>
          </div>
          <div className="access-panel-tools">
            <label>
              Modulo
              <select value={selectedModuleFilter} onChange={(event) => setSelectedModuleFilter(event.target.value)}>
                <option value="">Todos os modulos</option>
                {moduleOptions.map((module) => (
                  <option key={module.id_modulo} value={module.id_modulo}>
                    {module.titulo}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {filteredAccesses.length === 0 ? (
          <EmptyState title="Sem acessos recentes" text="Os acessos aparecem aqui depois que o usuario navega pela plataforma." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Tela</th>
                  <th>Inicio</th>
                  <th>Ultimo evento</th>
                  <th>Tempo ativo</th>
                  <th>Total acumulado</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccesses.map((access) => (
                  <tr key={`${access.email}-${access.inicio}-${access.tela}`}>
                    <td>{access.nome}</td>
                    <td>{access.email}</td>
                    <td>{access.tela}</td>
                    <td>{formatDate(access.inicio)}</td>
                    <td>{formatDate(access.ultimo_evento || access.fim)}</td>
                    <td>{formatDuration(access.tempo_ativo_segundos)}</td>
                    <td>{formatDuration(access.tempo_acumulado_segundos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function UsersSection({
  users,
  centers,
  onChanged
}: {
  users: AppUser[];
  centers: CostCenter[];
  onChanged: () => Promise<void>;
}) {
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({
    nome: "",
    email: "",
    password: "",
    perfil: "student",
    status: "active",
    centros_custo_permitidos: [] as string[]
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (editingId) {
      const payload: Record<string, unknown> = { ...form };
      if (!form.password) delete payload.password;
      await api.updateUser(editingId, payload);
    } else {
      await api.createUser(form);
    }
    setEditingId("");
    setForm({ nome: "", email: "", password: "", perfil: "student", status: "active", centros_custo_permitidos: [] });
    await onChanged();
  }

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Perfis e permissoes</span>
          <h1>Usuarios</h1>
        </div>
      </div>
      <div className="editor-layout">
        <form className="panel form-grid" onSubmit={submit}>
          <h2>{editingId ? "Editar usuario" : "Novo usuario"}</h2>
          <label>
            Nome
            <input value={form.nome} onChange={(event) => setForm({ ...form, nome: event.target.value })} required />
          </label>
          <label>
            E-mail
            <input
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              type="email"
              required
            />
          </label>
          <label>
            {editingId ? "Redefinir senha" : "Senha"}
            <input
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              type="password"
              required={!editingId}
              minLength={8}
              placeholder={editingId ? "Preencha apenas para trocar a senha" : ""}
            />
          </label>
          <label>
            Perfil
            <select value={form.perfil} onChange={(event) => setForm({ ...form, perfil: event.target.value })}>
              <option value="student">Aluno</option>
              <option value="admin">Administrador</option>
            </select>
          </label>
          <label>
            Status
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </label>
          <CenterPicker
            centers={centers}
            value={form.centros_custo_permitidos}
            onChange={(value) => setForm({ ...form, centros_custo_permitidos: value })}
          />
          <button className="primary-button" type="submit">
            <Save size={18} />
            Salvar usuario
          </button>
        </form>
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {users.map((item) => (
                <tr key={item.id_usuario}>
                  <td>{item.nome}</td>
                  <td>{item.email}</td>
                  <td>{item.perfil === "admin" ? "Admin" : "Aluno"}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td className="row-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(item.id_usuario);
                        setForm({
                          nome: item.nome,
                          email: item.email,
                          password: "",
                          perfil: item.perfil,
                          status: item.status,
                          centros_custo_permitidos: item.centros_custo_permitidos
                        });
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm("Excluir usuario?")) {
                          await api.deleteUser(item.id_usuario);
                          await onChanged();
                        }
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CentersSection({ centers, onChanged }: { centers: CostCenter[]; onChanged: () => Promise<void> }) {
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({ nome_cc: "", descricao: "", status: "active" });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (editingId) await api.updateCostCenter(editingId, form);
    else await api.createCostCenter(form);
    setEditingId("");
    setForm({ nome_cc: "", descricao: "", status: "active" });
    await onChanged();
  }

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Segmentacao</span>
          <h1>Centros de Custo</h1>
        </div>
      </div>
      <div className="editor-layout">
        <form className="panel form-grid" onSubmit={submit}>
          <h2>{editingId ? "Editar centro" : "Novo centro"}</h2>
          <label>
            Nome
            <input
              value={form.nome_cc}
              onChange={(event) => setForm({ ...form, nome_cc: event.target.value })}
              required
            />
          </label>
          <label>
            Descricao
            <textarea value={form.descricao} onChange={(event) => setForm({ ...form, descricao: event.target.value })} />
          </label>
          <label>
            Status
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </label>
          <button className="primary-button" type="submit">
            <Save size={18} />
            Salvar centro
          </button>
        </form>
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {centers.map((center) => (
                <tr key={center.id_cc}>
                  <td>{center.nome_cc}</td>
                  <td><StatusBadge status={center.status} /></td>
                  <td className="row-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(center.id_cc);
                        setForm({
                          nome_cc: center.nome_cc,
                          descricao: center.descricao,
                          status: normalizeUserStatus(center.status)
                        });
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm("Excluir centro de custo?")) {
                          await api.deleteCostCenter(center.id_cc);
                          await onChanged();
                        }
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ModulesSection({
  centers,
  modules,
  selectedModule,
  selectedModuleId,
  onSelectModule,
  contents,
  assessments,
  selectedAssessment,
  selectedAssessmentId,
  onSelectAssessment,
  questions,
  onChanged
}: {
  centers: CostCenter[];
  modules: TrainingModule[];
  selectedModule?: TrainingModule;
  selectedModuleId: string;
  onSelectModule: (id: string) => void;
  contents: ModuleContent[];
  assessments: Assessment[];
  selectedAssessment?: Assessment;
  selectedAssessmentId: string;
  onSelectAssessment: (id: string) => void;
  questions: Question[];
  onChanged: () => Promise<void>;
}) {
  const [moduleDetailTab, setModuleDetailTab] = useState<"overview" | "contents" | "assessments" | "preview">("overview");
  const [moduleSearch, setModuleSearch] = useState("");
  const [moduleStatusFilter, setModuleStatusFilter] = useState("all");
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [moduleEditId, setModuleEditId] = useState("");
  const [moduleForm, setModuleForm] = useState(emptyModuleForm);
  const [moduleSaving, setModuleSaving] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [contentEditId, setContentEditId] = useState("");
  const [contentForm, setContentForm] = useState(emptyContentForm);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [contentError, setContentError] = useState("");
  const [contentSaving, setContentSaving] = useState(false);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [assessmentEditId, setAssessmentEditId] = useState("");
  const [assessmentForm, setAssessmentForm] = useState(emptyAssessmentForm);
  const [assessmentSaving, setAssessmentSaving] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionEditId, setQuestionEditId] = useState("");
  const [questionForm, setQuestionForm] = useState(emptyQuestionForm);
  const [questionSaving, setQuestionSaving] = useState(false);
  const questionAlternativeFields = useMemo(() => {
    const values = questionForm.alternativas.split("\n");
    return values.some((item) => item.trim()) ? values : ["", ""];
  }, [questionForm.alternativas]);
  const questionAlternatives = useMemo(
    () =>
      questionAlternativeFields
        .map((item) => item.trim())
        .filter(Boolean),
    [questionAlternativeFields]
  );
  const filteredModules = useMemo(
    () =>
      modules.filter((module) => {
        const matchesSearch = `${module.titulo} ${module.descricao}`.toLowerCase().includes(moduleSearch.toLowerCase());
        const matchesStatus = moduleStatusFilter === "all" || module.status === moduleStatusFilter;
        return matchesSearch && matchesStatus;
      }),
    [modules, moduleSearch, moduleStatusFilter]
  );
  const drawerOpen = showModuleForm || showContentForm || showAssessmentForm || showQuestionForm;

  function closeDrawers() {
    setShowModuleForm(false);
    setShowContentForm(false);
    setShowAssessmentForm(false);
    setShowQuestionForm(false);
  }

  function openModuleForm(module?: TrainingModule) {
    closeDrawers();
    if (module) {
      setModuleEditId(module.id_modulo);
      setModuleForm({
        titulo: module.titulo,
        descricao: module.descricao,
        ordem: module.ordem,
        status: module.status,
        centros_custo_permitidos: module.centros_custo_permitidos,
        visivel_todos: module.visivel_todos
      });
    } else {
      setModuleEditId("");
      setModuleForm(emptyModuleForm);
    }
    setShowModuleForm(true);
  }

  function openContentForm(content?: ModuleContent) {
    closeDrawers();
    setContentError("");
    setFile(null);
    setFileInputKey((key) => key + 1);
    if (content) {
      setContentEditId(content.id_conteudo);
      setContentForm({
        tipo_conteudo: content.tipo_conteudo,
        titulo: content.titulo,
        texto: content.texto,
        url_arquivo: content.url_arquivo,
        ordem: content.ordem,
        obrigatorio: content.obrigatorio,
        status: content.status,
        centros_custo_permitidos: content.centros_custo_permitidos,
        visivel_todos: content.visivel_todos
      });
    } else {
      setContentEditId("");
      setContentForm({ ...emptyContentForm, ordem: nextOrder(contents) });
    }
    setShowContentForm(true);
  }

  function openAssessmentForm(assessment?: Assessment) {
    closeDrawers();
    setModuleDetailTab("assessments");
    if (assessment) {
      setAssessmentEditId(assessment.id_avaliacao);
      setAssessmentForm({
        titulo: assessment.titulo,
        descricao: assessment.descricao,
        nota_minima: assessment.nota_minima,
        status: assessment.status,
        mostrar_resultado: assessment.mostrar_resultado
      });
    } else {
      setAssessmentEditId("");
      setAssessmentForm(emptyAssessmentForm);
    }
    setShowAssessmentForm(true);
  }

  function openQuestionForm(question?: Question) {
    closeDrawers();
    setModuleDetailTab("assessments");
    if (question) {
      setQuestionEditId(question.id_pergunta);
      setQuestionForm({
        tipo: question.tipo,
        enunciado: question.enunciado,
        alternativas: question.alternativas.join("\n"),
        resposta_correta: question.resposta_correta ?? "",
        pontos: question.pontos,
        ordem: question.ordem,
        feedback: question.feedback,
        url_imagem: question.url_imagem,
        id_imagem_drive: question.id_imagem_drive
      });
    } else {
      setQuestionEditId("");
      setQuestionForm({ ...emptyQuestionForm, ordem: nextOrder(questions) });
    }
    setShowQuestionForm(true);
  }

  function updateQuestionAlternative(index: number, value: string) {
    const next = [...questionAlternativeFields];
    next[index] = value;
    const cleaned = next.map((item) => item.trim()).filter(Boolean);
    setQuestionForm({
      ...questionForm,
      alternativas: next.join("\n"),
      resposta_correta: cleaned.includes(questionForm.resposta_correta) ? questionForm.resposta_correta : ""
    });
  }

  function removeQuestionAlternative(index: number) {
    const next = questionAlternativeFields.filter((_, itemIndex) => itemIndex !== index);
    const cleaned = next.map((item) => item.trim()).filter(Boolean);
    setQuestionForm({
      ...questionForm,
      alternativas: next.join("\n"),
      resposta_correta: cleaned.includes(questionForm.resposta_correta) ? questionForm.resposta_correta : ""
    });
  }

  function selectFile(nextFile: File | null) {
    setFile(nextFile);
    if (!nextFile) return;
    setContentForm({
      ...contentForm,
      tipo_conteudo: inferContentType(nextFile),
      titulo: contentForm.titulo || stripFileExtension(nextFile.name)
    });
  }

  async function submitModule(event: FormEvent) {
    event.preventDefault();
    if (moduleSaving) return;
    setModuleSaving(true);
    try {
      if (moduleEditId) await api.updateModule(moduleEditId, moduleForm);
      else await api.createModule(moduleForm);
      setModuleEditId("");
      setModuleForm(emptyModuleForm);
      setShowModuleForm(false);
      await onChanged();
    } finally {
      setModuleSaving(false);
    }
  }

  async function submitContent(event: FormEvent) {
    event.preventDefault();
    if (!selectedModuleId) return;
    setContentError("");
    setContentSaving(true);
    try {
      if (file) {
        const formData = new FormData();
        Object.entries(contentForm).forEach(([key, value]) => {
          formData.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
        });
        formData.append("file", file);
        await api.uploadContent(selectedModuleId, formData);
      } else if (contentEditId) {
        await api.updateContent(contentEditId, contentForm);
      } else {
        await api.createContent(selectedModuleId, contentForm);
      }
      setContentEditId("");
      setContentForm(emptyContentForm);
      setFile(null);
      setFileInputKey((key) => key + 1);
      setShowContentForm(false);
      await onChanged();
    } catch (error) {
      setContentError(error instanceof Error ? error.message : "Nao foi possivel salvar o conteudo.");
    } finally {
      setContentSaving(false);
    }
  }

  async function submitAssessment(event: FormEvent) {
    event.preventDefault();
    if (!selectedModuleId || assessmentSaving) return;
    setAssessmentSaving(true);
    try {
      if (assessmentEditId) await api.updateAssessment(assessmentEditId, assessmentForm);
      else await api.createAssessment(selectedModuleId, assessmentForm);
      setAssessmentEditId("");
      setAssessmentForm(emptyAssessmentForm);
      setShowAssessmentForm(false);
      await onChanged();
    } finally {
      setAssessmentSaving(false);
    }
  }

  async function submitQuestion(event: FormEvent) {
    event.preventDefault();
    if (!selectedAssessmentId || questionSaving) return;
    setQuestionSaving(true);
    const payload = {
      ...questionForm,
      alternativas: questionForm.tipo === "multiple_choice" ? questionAlternatives : [],
      resposta_correta: questionForm.tipo === "essay" ? "" : questionForm.resposta_correta
    };
    try {
      if (questionEditId) await api.updateQuestion(questionEditId, payload);
      else await api.createQuestion(selectedAssessmentId, payload);
      setQuestionEditId("");
      setQuestionForm(emptyQuestionForm);
      setShowQuestionForm(false);
      await onChanged();
    } finally {
      setQuestionSaving(false);
    }
  }

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Construtor de treinamento</span>
          <h1>Modulos</h1>
        </div>
      </div>
      <div className="module-workbench">
        <aside className="panel module-browser">
          <div className="module-browser-header">
            <div>
              <span>{modules.length} modulo(s)</span>
              <h2>Biblioteca</h2>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={() => openModuleForm()}
              title="Novo modulo"
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="module-browser-controls">
            <label className="search-field">
              <Search size={16} />
              <input
                value={moduleSearch}
                onChange={(event) => setModuleSearch(event.target.value)}
                placeholder="Buscar modulo"
              />
            </label>
            <label className="filter-field">
              <Settings2 size={16} />
              <select value={moduleStatusFilter} onChange={(event) => setModuleStatusFilter(event.target.value)}>
                <option value="all">Todos</option>
                <option value="published">Publicados</option>
                <option value="draft">Rascunhos</option>
                <option value="hidden">Ocultos</option>
                <option value="archived">Arquivados</option>
              </select>
            </label>
          </div>

          <div className="module-list-scroll">
            {filteredModules.length === 0 ? (
              <EmptyState title="Nenhum modulo" text="Ajuste a busca ou crie um novo modulo." />
            ) : (
              filteredModules.map((module) => (
                <button
                  key={module.id_modulo}
                  type="button"
                  className={`module-row module-library-row ${selectedModuleId === module.id_modulo ? "selected" : ""}`}
                  onClick={() => {
                    onSelectModule(module.id_modulo);
                    setModuleDetailTab("overview");
                    closeDrawers();
                  }}
                >
                  <div>
                    <strong>{module.titulo}</strong>
                    <span>{module.descricao || moduleAccessLabel(module, centers)}</span>
                    <small>
                      {module.total_conteudos ?? 0} conteudo(s) · {moduleAccessLabel(module, centers)}
                    </small>
                  </div>
                  <StatusBadge status={module.status} />
                </button>
              ))
            )}
          </div>
        </aside>

        <div className="panel module-builder">
          {!selectedModule ? (
            <EmptyState title="Selecione um modulo" text="Os detalhes aparecem aqui." />
          ) : (
            <>
              <div className="module-builder-header">
                <div>
                  <span>{moduleAccessLabel(selectedModule, centers)}</span>
                  <h2>{selectedModule.titulo}</h2>
                  <p>{selectedModule.descricao}</p>
                </div>
                <div className="row-actions">
                  <button type="button" title="Editar modulo" onClick={() => openModuleForm(selectedModule)}>
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    title="Excluir modulo"
                    onClick={async () => {
                      if (window.confirm("Excluir modulo, dados relacionados e arquivos anexados do Drive?")) {
                        await api.deleteModule(selectedModule.id_modulo);
                        await onChanged();
                      }
                    }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="module-builder-stats">
                <div>
                  <span>Conteudos</span>
                  <strong>{contents.length}</strong>
                </div>
                <div>
                  <span>Avaliacoes</span>
                  <strong>{assessments.length}</strong>
                </div>
                <div>
                  <span>Perguntas</span>
                  <strong>{questions.length}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <StatusBadge status={selectedModule.status} />
                </div>
              </div>

              <div className="module-detail-tabs builder-tabs" role="tablist" aria-label="Detalhes do modulo">
                <button
                  type="button"
                  className={moduleDetailTab === "overview" ? "active" : ""}
                  onClick={() => setModuleDetailTab("overview")}
                >
                  Dados
                </button>
                <button
                  type="button"
                  className={moduleDetailTab === "contents" ? "active" : ""}
                  onClick={() => setModuleDetailTab("contents")}
                >
                  Conteudos
                </button>
                <button
                  type="button"
                  className={moduleDetailTab === "assessments" ? "active" : ""}
                  onClick={() => setModuleDetailTab("assessments")}
                >
                  Avaliacao
                </button>
                <button
                  type="button"
                  className={moduleDetailTab === "preview" ? "active" : ""}
                  onClick={() => setModuleDetailTab("preview")}
                >
                  Previa
                </button>
              </div>

              {moduleDetailTab === "overview" ? (
                <ModuleOverview module={selectedModule} contents={contents} assessments={assessments} questions={questions} centers={centers} />
              ) : null}

              {moduleDetailTab === "contents" ? (
                <div className="builder-section">
                  <div className="editor-toolbar">
                    <div>
                      <h3>Conteudos</h3>
                      <span>{contents.length} item(ns) no modulo</span>
                    </div>
                    <button className="secondary-button" type="button" onClick={() => openContentForm()}>
                      <Upload size={18} />
                      Adicionar
                    </button>
                  </div>
                  {contents.length === 0 ? (
                    <EmptyState title="Sem conteudos" text="Inclua textos, links, arquivos, imagens ou videos." />
                  ) : (
                    <div className="builder-list">
                      {contents.map((content) => (
                        <article className="builder-list-row" key={content.id_conteudo}>
                          <div className="builder-row-icon">
                            <FileText size={18} />
                          </div>
                          <div>
                            <strong>{content.titulo}</strong>
                            <span>
                              {contentTypeLabel(content.tipo_conteudo)} · ordem {content.ordem} · {content.obrigatorio ? "Obrigatorio" : "Opcional"}
                            </span>
                            {content.nome_arquivo ? <small>{content.nome_arquivo}</small> : null}
                          </div>
                          <StatusBadge status={content.status} />
                          <div className="row-actions">
                            <button type="button" title="Editar conteudo" onClick={() => openContentForm(content)}>
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              title="Excluir conteudo"
                              onClick={async () => {
                                if (window.confirm("Excluir conteudo e arquivo anexado do Drive?")) {
                                  await api.deleteContent(content.id_conteudo, true);
                                  await onChanged();
                                }
                              }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {moduleDetailTab === "assessments" ? (
                <div className="builder-section">
                  <div className="editor-toolbar">
                    <div>
                      <h3>Avaliacao</h3>
                      <span>{assessments.length} avaliacao(oes), {questions.length} pergunta(s)</span>
                    </div>
                    <button className="secondary-button" type="button" onClick={() => openAssessmentForm()}>
                      <Plus size={18} />
                      Nova avaliacao
                    </button>
                  </div>
                  <div className="assessment-builder-grid">
                    <div className="builder-list">
                      {assessments.length === 0 ? (
                        <EmptyState title="Sem avaliacoes" text="Crie uma avaliacao quando este modulo precisar de prova." />
                      ) : (
                        assessments.map((assessment) => (
                          <article
                            className={`builder-list-row ${selectedAssessmentId === assessment.id_avaliacao ? "selected-mini-card" : ""}`}
                            key={assessment.id_avaliacao}
                          >
                            <button className="plain-select" type="button" onClick={() => onSelectAssessment(assessment.id_avaliacao)}>
                              <strong>{assessment.titulo}</strong>
                              <span>Nota minima {assessment.nota_minima} · {assessment.mostrar_resultado ? "resultado visivel" : "resultado oculto"}</span>
                            </button>
                            <StatusBadge status={assessment.status} />
                            <div className="row-actions">
                              <button type="button" title="Editar avaliacao" onClick={() => openAssessmentForm(assessment)}>
                                <Pencil size={15} />
                              </button>
                              <button
                                type="button"
                                title="Excluir avaliacao"
                                onClick={async () => {
                                  if (window.confirm("Excluir avaliacao e perguntas relacionadas?")) {
                                    await api.deleteAssessment(assessment.id_avaliacao);
                                    await onChanged();
                                  }
                                }}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </article>
                        ))
                      )}
                    </div>
                    <div className="question-panel">
                      <div className="editor-toolbar">
                        <div>
                          <h3>Perguntas</h3>
                          <span>{selectedAssessment?.titulo ?? "Selecione uma avaliacao"}</span>
                        </div>
                        <button className="secondary-button" type="button" disabled={!selectedAssessment} onClick={() => openQuestionForm()}>
                          <Plus size={18} />
                          Nova pergunta
                        </button>
                      </div>
                      {!selectedAssessment ? (
                        <EmptyState title="Nenhuma avaliacao selecionada" text="Escolha uma avaliacao para editar as perguntas." />
                      ) : questions.length === 0 ? (
                        <EmptyState title="Sem perguntas" text="Crie questoes objetivas ou discursivas." />
                      ) : (
                        <div className="builder-list">
                          {questions.map((question) => (
                            <article className="question-row" key={question.id_pergunta}>
                              <div>
                                <strong>{question.enunciado}</strong>
                                <span>
                                  {questionTypeLabel(question.tipo)} · {question.pontos} ponto(s) · ordem {question.ordem}
                                </span>
                              </div>
                              <div className="row-actions">
                                <button type="button" title="Editar pergunta" onClick={() => openQuestionForm(question)}>
                                  <Pencil size={15} />
                                </button>
                                <button
                                  type="button"
                                  title="Excluir pergunta"
                                  onClick={async () => {
                                    if (window.confirm("Excluir pergunta?")) {
                                      await api.deleteQuestion(question.id_pergunta);
                                      await onChanged();
                                    }
                                  }}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {moduleDetailTab === "preview" ? (
                <ModuleStudentPreview module={selectedModule} contents={contents} assessments={assessments} questions={questions} />
              ) : null}
            </>
          )}
        </div>
      </div>

      {drawerOpen ? (
        <FormDrawer
          title={
            showModuleForm
              ? moduleEditId
                ? "Editar modulo"
                : "Novo modulo"
              : showContentForm
                ? contentEditId
                  ? "Editar conteudo"
                  : "Adicionar conteudo"
                : showAssessmentForm
                  ? assessmentEditId
                    ? "Editar avaliacao"
                    : "Nova avaliacao"
                  : questionEditId
                    ? "Editar pergunta"
                    : "Nova pergunta"
          }
          onClose={closeDrawers}
        >
          {showModuleForm ? (
            <form className="compact-form" onSubmit={submitModule}>
              <label>
                Titulo
                <input
                  value={moduleForm.titulo}
                  onChange={(event) => setModuleForm({ ...moduleForm, titulo: event.target.value })}
                  required
                />
              </label>
              <label>
                Descricao
                <textarea value={moduleForm.descricao} onChange={(event) => setModuleForm({ ...moduleForm, descricao: event.target.value })} />
              </label>
              <div className="inline-fields">
                <label>
                  Ordem
                  <input value={moduleForm.ordem} onChange={(event) => setModuleForm({ ...moduleForm, ordem: Number(event.target.value) })} type="number" />
                </label>
                <label>
                  Status
                <select
                  value={moduleForm.status}
                  onChange={(event) => setModuleForm({ ...moduleForm, status: event.target.value })}
                >
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                  <option value="hidden">Oculto</option>
                  <option value="archived">Arquivado</option>
                </select>
                </label>
              </div>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={moduleForm.visivel_todos}
                  onChange={(event) => setModuleForm({ ...moduleForm, visivel_todos: event.target.checked })}
                />
                Visivel para todos
              </label>
              {!moduleForm.visivel_todos ? (
                <CenterPicker
                  centers={centers}
                  value={moduleForm.centros_custo_permitidos}
                  onChange={(value) => setModuleForm({ ...moduleForm, centros_custo_permitidos: value })}
                />
              ) : null}
              <div className="form-actions">
                <button className="primary-button" type="submit" disabled={moduleSaving}>
                  <Save size={18} />
                  {moduleSaving ? "Salvando..." : "Salvar modulo"}
                </button>
                <button className="secondary-button" type="button" onClick={closeDrawers} disabled={moduleSaving}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : null}

          {showContentForm ? (
            <form className="compact-form" onSubmit={submitContent}>
              <label>
                Titulo
                <input value={contentForm.titulo} onChange={(event) => setContentForm({ ...contentForm, titulo: event.target.value })} required />
              </label>
              <label>
                Tipo
                <select value={contentForm.tipo_conteudo} onChange={(event) => setContentForm({ ...contentForm, tipo_conteudo: event.target.value })}>
                  <option value="text">Texto</option>
                  <option value="file">Arquivo</option>
                  <option value="image">Imagem</option>
                  <option value="video">Video</option>
                  <option value="link">Link</option>
                  <option value="material">Material complementar</option>
                </select>
              </label>
              <label className="file-dropzone">
                <Upload size={20} />
                <strong>{file ? file.name : "Selecionar arquivo"}</strong>
                <span>{file ? `${Math.round(file.size / 1024)} KB` : "Documentos, imagens ou videos"}</span>
                <input key={fileInputKey} type="file" onChange={(event) => selectFile(event.target.files?.[0] ?? null)} />
              </label>
              <label>
                Texto
                <textarea value={contentForm.texto} onChange={(event) => setContentForm({ ...contentForm, texto: event.target.value })} />
              </label>
              <label>
                Link externo
                <input value={contentForm.url_arquivo} onChange={(event) => setContentForm({ ...contentForm, url_arquivo: event.target.value })} />
              </label>
              {contentError ? <div className="form-error">{contentError}</div> : null}
              <div className="inline-fields">
                <label>
                  Ordem
                  <input value={contentForm.ordem} onChange={(event) => setContentForm({ ...contentForm, ordem: Number(event.target.value) })} type="number" />
                </label>
                <label>
                  Status
                  <select value={contentForm.status} onChange={(event) => setContentForm({ ...contentForm, status: event.target.value })}>
                    <option value="draft">Rascunho</option>
                    <option value="published">Publicado</option>
                    <option value="hidden">Oculto</option>
                  </select>
                </label>
              </div>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={contentForm.obrigatorio}
                  onChange={(event) => setContentForm({ ...contentForm, obrigatorio: event.target.checked })}
                />
                Obrigatorio
              </label>
              <div className="form-actions">
                <button className="primary-button" type="submit" disabled={contentSaving}>
                  <Upload size={18} />
                  {contentSaving ? "Enviando..." : "Salvar conteudo"}
                </button>
                <button className="secondary-button" type="button" onClick={closeDrawers} disabled={contentSaving}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : null}

          {showAssessmentForm ? (
            <form className="compact-form" onSubmit={submitAssessment}>
              <label>
                Titulo
                <input value={assessmentForm.titulo} onChange={(event) => setAssessmentForm({ ...assessmentForm, titulo: event.target.value })} required />
              </label>
              <label>
                Descricao
                <textarea value={assessmentForm.descricao} onChange={(event) => setAssessmentForm({ ...assessmentForm, descricao: event.target.value })} />
              </label>
              <div className="inline-fields">
                <label>
                  Nota minima
                  <input
                    value={assessmentForm.nota_minima}
                    onChange={(event) => setAssessmentForm({ ...assessmentForm, nota_minima: Number(event.target.value) })}
                    type="number"
                  />
                </label>
                <label>
                  Status
                  <select value={assessmentForm.status} onChange={(event) => setAssessmentForm({ ...assessmentForm, status: event.target.value })}>
                    <option value="draft">Rascunho</option>
                    <option value="published">Publicado</option>
                    <option value="hidden">Oculto</option>
                  </select>
                </label>
              </div>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={assessmentForm.mostrar_resultado}
                  onChange={(event) => setAssessmentForm({ ...assessmentForm, mostrar_resultado: event.target.checked })}
                />
                Mostrar resultado ao aluno
              </label>
              <div className="form-actions">
                <button className="primary-button" type="submit" disabled={assessmentSaving}>
                  <Save size={18} />
                  {assessmentSaving ? "Salvando..." : "Salvar avaliacao"}
                </button>
                <button className="secondary-button" type="button" onClick={closeDrawers} disabled={assessmentSaving}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : null}

          {showQuestionForm ? (
            <form className="compact-form" onSubmit={submitQuestion}>
              <label>
                Tipo de pergunta
                <select
                  value={questionForm.tipo}
                  onChange={(event) => {
                    const tipo = event.target.value as QuestionType;
                    setQuestionForm({
                      ...questionForm,
                      tipo,
                      alternativas: tipo === "multiple_choice" ? questionForm.alternativas : "",
                      resposta_correta: tipo === "true_false" ? "true" : ""
                    });
                  }}
                >
                  <option value="multiple_choice">Multipla escolha</option>
                  <option value="true_false">Verdadeiro ou falso</option>
                  <option value="short_answer">Resposta curta</option>
                  <option value="essay">Discursiva</option>
                </select>
              </label>
              <label>
                Enunciado
                <textarea value={questionForm.enunciado} onChange={(event) => setQuestionForm({ ...questionForm, enunciado: event.target.value })} required />
              </label>
              {questionForm.tipo === "multiple_choice" ? (
                <div className="alternative-editor">
                  <span>Alternativas</span>
                  {questionAlternativeFields.map((option, index) => {
                    const cleanOption = option.trim();
                    return (
                      <div className="alternative-row" key={`${index}-${questionAlternativeFields.length}`}>
                        <input value={option} onChange={(event) => updateQuestionAlternative(index, event.target.value)} placeholder={`Alternativa ${index + 1}`} required={index < 2} />
                        <label className="answer-radio" title="Marcar como correta">
                          <input
                            type="radio"
                            name="correct-answer"
                            disabled={!cleanOption}
                            checked={Boolean(cleanOption) && questionForm.resposta_correta === cleanOption}
                            onChange={() => setQuestionForm({ ...questionForm, resposta_correta: cleanOption })}
                          />
                          Correta
                        </label>
                        <button type="button" title="Remover alternativa" onClick={() => removeQuestionAlternative(index)} disabled={questionAlternativeFields.length <= 2}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => setQuestionForm({ ...questionForm, alternativas: [...questionAlternativeFields, ""].join("\n") })}
                  >
                    <Plus size={16} />
                    Adicionar alternativa
                  </button>
                </div>
              ) : null}
              {questionForm.tipo === "true_false" ? (
                <label>
                  Resposta correta
                  <select value={questionForm.resposta_correta} onChange={(event) => setQuestionForm({ ...questionForm, resposta_correta: event.target.value })} required>
                    <option value="true">Verdadeiro</option>
                    <option value="false">Falso</option>
                  </select>
                </label>
              ) : null}
              {questionForm.tipo === "short_answer" ? (
                <label>
                  Resposta esperada
                  <input value={questionForm.resposta_correta} onChange={(event) => setQuestionForm({ ...questionForm, resposta_correta: event.target.value })} required />
                </label>
              ) : null}
              <div className="inline-fields">
                <label>
                  Pontos
                  <input value={questionForm.pontos} onChange={(event) => setQuestionForm({ ...questionForm, pontos: Number(event.target.value) })} type="number" min="0" />
                </label>
                <label>
                  Ordem
                  <input value={questionForm.ordem} onChange={(event) => setQuestionForm({ ...questionForm, ordem: Number(event.target.value) })} type="number" min="0" />
                </label>
              </div>
              <label>
                Imagem por URL
                <input value={questionForm.url_imagem} onChange={(event) => setQuestionForm({ ...questionForm, url_imagem: event.target.value })} />
              </label>
              <label>
                Feedback
                <textarea value={questionForm.feedback} onChange={(event) => setQuestionForm({ ...questionForm, feedback: event.target.value })} />
              </label>
              <div className="form-actions">
                <button className="primary-button" type="submit" disabled={questionSaving}>
                  <Save size={18} />
                  {questionSaving ? "Salvando..." : "Salvar pergunta"}
                </button>
                <button className="secondary-button" type="button" onClick={closeDrawers} disabled={questionSaving}>
                  Cancelar
                </button>
              </div>
            </form>
          ) : null}
        </FormDrawer>
      ) : null}
    </section>
  );
}

function FormDrawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="drawer-backdrop">
      <aside className="form-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="icon-button" type="button" onClick={onClose} title="Fechar">
            <X size={18} />
          </button>
        </header>
        {children}
      </aside>
    </div>
  );
}

function ModuleOverview({
  module,
  contents,
  assessments,
  questions,
  centers
}: {
  module: TrainingModule;
  contents: ModuleContent[];
  assessments: Assessment[];
  questions: Question[];
  centers: CostCenter[];
}) {
  return (
    <div className="module-overview-grid">
      <div className="overview-block">
        <span>Status</span>
        <StatusBadge status={module.status} />
      </div>
      <div className="overview-block">
        <span>Publico</span>
        <strong>{moduleAccessLabel(module, centers)}</strong>
      </div>
      <div className="overview-block">
        <span>Ordem</span>
        <strong>{module.ordem}</strong>
      </div>
      <div className="overview-block">
        <span>Estrutura</span>
        <strong>
          {contents.length} conteudo(s), {assessments.length} avaliacao(oes), {questions.length} pergunta(s)
        </strong>
      </div>
    </div>
  );
}

function ModuleStudentPreview({
  module,
  contents,
  assessments,
  questions
}: {
  module: TrainingModule;
  contents: ModuleContent[];
  assessments: Assessment[];
  questions: Question[];
}) {
  return (
    <div className="student-preview">
      <div className="student-preview-header">
        <div>
          <span>Previa do aluno</span>
          <h2>{module.titulo}</h2>
          <p>{module.descricao}</p>
        </div>
        <Badge tone="blue">{module.progresso_percentual ?? 0}%</Badge>
      </div>
      <div className="preview-timeline">
        {contents.length === 0 ? (
          <EmptyState title="Sem conteudos" text="A previa sera preenchida quando houver conteudos." />
        ) : (
          contents.map((content, index) => (
            <article className="preview-item" key={content.id_conteudo}>
              <div className="preview-index">{index + 1}</div>
              <div>
                <strong>{content.titulo}</strong>
                <span>
                  {contentTypeLabel(content.tipo_conteudo)} · {content.obrigatorio ? "Obrigatorio" : "Opcional"}
                </span>
                {content.texto ? <p>{content.texto}</p> : null}
                {content.nome_arquivo ? <small>{content.nome_arquivo}</small> : null}
              </div>
              <StatusBadge status={content.status} />
            </article>
          ))
        )}
      </div>
      <div className="preview-assessments">
        <h3>Avaliacoes</h3>
        {assessments.length === 0 ? (
          <EmptyState title="Sem avaliacao" text="Nenhuma avaliacao cadastrada para este modulo." />
        ) : (
          assessments.map((assessment) => (
            <article className="preview-assessment" key={assessment.id_avaliacao}>
              <div>
                <strong>{assessment.titulo}</strong>
                <span>
                  Nota minima {assessment.nota_minima} · {questions.filter((question) => question.id_avaliacao === assessment.id_avaliacao).length} pergunta(s)
                </span>
              </div>
              <Eye size={18} />
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function ResultsSection({
  results,
  selectedAttemptId,
  onSelectAttempt,
  responses,
  onChanged
}: {
  results: ResultAttempt[];
  selectedAttemptId: string;
  onSelectAttempt: (id: string) => Promise<void>;
  responses: Record<string, unknown>[];
  onChanged: () => Promise<void>;
}) {
  const pendingManual = results.filter((result) => result.status_correcao === "pending_manual");

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Notas e exportacao</span>
          <h1>Resultados</h1>
        </div>
        <a className="secondary-button" href="/api/admin/results.csv">
          <Download size={18} />
          Exportar CSV
        </a>
      </div>
      <div className="panel">
        <h2>Discursivas pendentes</h2>
        {pendingManual.length === 0 ? (
          <EmptyState title="Sem discursivas pendentes" text="As questoes discursivas enviadas aparecem aqui para correcao." />
        ) : (
          <div className="table-wrap compact-table">
            <table>
              <thead>
                <tr>
                  <th>Aluno</th>
                  <th>Centro de Custo</th>
                  <th>Avaliacao</th>
                  <th>Nota parcial</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {pendingManual.map((result) => (
                  <tr key={`pending-${result.id_tentativa}`}>
                    <td>{result.usuario_nome}</td>
                    <td>{result.usuario_centros_custo?.join(", ") || "-"}</td>
                    <td>{result.avaliacao_titulo}</td>
                    <td>{result.percentual ?? "-"}%</td>
                    <td>
                      <button type="button" onClick={() => onSelectAttempt(result.id_tentativa)}>
                        Corrigir discursiva
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="two-column">
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Centro de Custo</th>
                <th>Modulo</th>
                <th>Nota</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.id_tentativa}>
                  <td>{result.usuario_nome}</td>
                  <td>{result.usuario_centros_custo?.join(", ") || "-"}</td>
                  <td>{result.modulo_titulo}</td>
                  <td>
                    {result.status_correcao === "pending_manual" ? "Parcial " : ""}
                    {result.percentual ?? "-"}%
                  </td>
                  <td><CorrectionBadge status={result.status_correcao} /></td>
                  <td>
                    <button type="button" onClick={() => onSelectAttempt(result.id_tentativa)}>
                      Ver respostas
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="panel">
          <h2>Respostas</h2>
          {!selectedAttemptId ? (
            <EmptyState title="Selecione uma tentativa" text="Use a tabela ao lado para abrir as respostas." />
          ) : (
            <div className="list-stack">
              <h3>Discursivas</h3>
              {responses.filter(isEssayResponse).length === 0 ? (
                <EmptyState title="Sem discursivas" text="Esta tentativa nao possui questoes discursivas." />
              ) : (
                responses
                  .filter(isEssayResponse)
                  .map((response) => (
                    <ResponseCard key={String(response.id_resposta)} response={response} onChanged={onChanged} />
                  ))
              )}
              <h3>Objetivas</h3>
              {responses.filter((response) => !isEssayResponse(response)).map((response) => (
                <ResponseCard key={String(response.id_resposta)} response={response} onChanged={onChanged} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ResponseCard({ response, onChanged }: { response: Record<string, unknown>; onChanged: () => Promise<void> }) {
  const question = response.pergunta as { enunciado?: string; pontos?: number; tipo?: string } | null;
  const isEssay = question?.tipo === "essay";
  const [score, setScore] = useState(Number(response.pontuacao_obtida ?? 0));
  const [feedback, setFeedback] = useState(String(response.feedback ?? ""));

  return (
    <div className="mini-card vertical">
      <strong>{question?.enunciado ?? "Pergunta removida"}</strong>
      <span>Resposta: {String(response.resposta_usuario ?? "")}</span>
      <span>
        Correcao: {isEssay ? essayStatusLabel(String(response.correta ?? "")) : objectiveStatusLabel(String(response.correta ?? ""))}
      </span>
      <span>
        Nota: {String(response.pontuacao_obtida ?? "0")} / {question?.pontos ?? 0}
      </span>
      {isEssay ? (
        <>
          <div className="inline-fields">
            <label>
              Nota da discursiva
              <input
                value={score}
                type="number"
                min="0"
                max={question?.pontos ?? undefined}
                onChange={(event) => setScore(Number(event.target.value))}
              />
            </label>
            <label>
              Feedback
              <input value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="Opcional" />
            </label>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={async () => {
              await api.manualGrade(String(response.id_resposta), { pontuacao_obtida: score, feedback });
              await onChanged();
            }}
          >
            Salvar correcao
          </button>
        </>
      ) : null}
    </div>
  );
}

function isEssayResponse(response: Record<string, unknown>) {
  const question = response.pergunta as { tipo?: string } | null;
  return question?.tipo === "essay";
}

function CorrectionBadge({ status }: { status: string }) {
  const tone = status === "pending_manual" ? "amber" : "green";
  return <Badge tone={tone}>{correctionStatusLabel(status)}</Badge>;
}

function correctionStatusLabel(status: string) {
  const labels: Record<string, string> = {
    auto_graded: "Corrigida automaticamente",
    pending_manual: "Aguardando discursiva",
    manually_graded: "Corrigida pelo admin"
  };
  return labels[status] ?? status;
}

function essayStatusLabel(status: string) {
  if (status === "pending") return "Aguardando correcao";
  return status === "true" ? "Corrigida com pontuacao" : "Corrigida sem pontuacao";
}

function objectiveStatusLabel(status: string) {
  return status === "true" ? "Correta" : "Incorreta";
}

function CenterPicker({
  centers,
  value,
  onChange
}: {
  centers: CostCenter[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const activeCenters = useMemo(() => centers.filter((center) => isActiveStatus(center.status)), [centers]);
  return (
    <fieldset className="picker">
      <legend>Centros de Custo</legend>
      {activeCenters.map((center) => (
        <label key={center.id_cc} className="check-row">
          <input
            type="checkbox"
            checked={value.includes(center.id_cc)}
            onChange={(event) => {
              if (event.target.checked) onChange([...value, center.id_cc]);
              else onChange(value.filter((id) => id !== center.id_cc));
            }}
          />
          {formatCostCenterName(center)}
        </label>
      ))}
    </fieldset>
  );
}

function nextOrder(items: { ordem: number }[]): number {
  return items.length === 0 ? 0 : Math.max(...items.map((item) => Number(item.ordem || 0))) + 1;
}

function inferContentType(file: File): ModuleContent["tipo_conteudo"] {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

function stripFileExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, "");
}

function contentTypeLabel(type: ModuleContent["tipo_conteudo"]): string {
  const labels: Record<ModuleContent["tipo_conteudo"], string> = {
    text: "Texto",
    file: "Arquivo",
    image: "Imagem",
    video: "Video",
    link: "Link",
    material: "Material"
  };
  return labels[type] ?? type;
}

function questionTypeLabel(type: QuestionType): string {
  const labels: Record<QuestionType, string> = {
    multiple_choice: "Multipla escolha",
    true_false: "Verdadeiro ou falso",
    short_answer: "Resposta curta",
    essay: "Discursiva"
  };
  return labels[type] ?? type;
}

function moduleAccessLabel(module: TrainingModule, centers: CostCenter[]): string {
  if (module.visivel_todos) return "Todos os Centros de Custo";
  if (module.centros_custo_permitidos.length === 0) return "Sem CC definido";
  const names = module.centros_custo_permitidos.map((id) => {
    const center = centers.find((item) => item.id_cc === id);
    return center ? formatCostCenterName(center) : id;
  });
  return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
}

function StatusBadge({ status }: { status: string }) {
  const normalized = status.trim().toLowerCase();
  const tone = status === "published" || isActiveStatus(status) ? "green" : status === "draft" ? "amber" : "neutral";
  const labels: Record<string, string> = {
    active: "Ativo",
    ativo: "Ativo",
    ativa: "Ativo",
    inactive: "Inativo",
    inativo: "Inativo",
    inativa: "Inativo",
    draft: "Rascunho",
    published: "Publicado",
    hidden: "Oculto",
    archived: "Arquivado"
  };
  return <Badge tone={tone}>{labels[normalized] ?? status}</Badge>;
}

function isActiveStatus(status: string): boolean {
  return ["active", "ativo", "ativa"].includes(status.trim().toLowerCase());
}

function normalizeUserStatus(status: string): "active" | "inactive" {
  return isActiveStatus(status) ? "active" : "inactive";
}

function formatCostCenterName(center: CostCenter): string {
  return center.descricao ? `${center.descricao} (${center.nome_cc})` : center.nome_cc;
}

function formatDate(value?: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function formatDuration(seconds?: number) {
  const total = Math.max(0, Math.round(seconds ?? 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;

  if (hours > 0) return `${hours}h ${minutes}min`;
  if (minutes > 0) return `${minutes}min ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}

function matchesModuleAccess(
  access: Dashboard["ultimos_acessos"][number],
  moduleId: string,
  modules: TrainingModule[]
): boolean {
  if (access.id_modulo) return access.id_modulo === moduleId;

  const module = modules.find((item) => item.id_modulo === moduleId);
  if (!module) return false;
  return normalizeModuleName(access.tela) === normalizeModuleName(module.titulo);
}

function normalizeModuleName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(admin|modulo|modulos)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/s\b/g, "");
}
